import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
} from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import {
  BeddingConfiguration,
  ExtraGuestGeneratedPolicies,
  ExtraGuestGeneratedPolicyItem,
  IncludedGuestEntry,
  RoomPolicy,
} from '~/types/roomPolicy';

import OffersService from '../../../services/OffersService';
import { reportError } from '../../../utils/reportError';
import ErrorListDisplay from '../ErrorListDisplay';

import RoomPolicyBeddingEditor from './RoomPolicyBeddingEditor';
import RoomPolicyGuestTable from './RoomPolicyGuestTable';
import RoomPolicyIncludedGuestsTable from './RoomPolicyIncludedGuestsTable';
import RoomPolicyListEditor from './RoomPolicyListEditor';
import { buttonMessages, buttonStates } from './states/submitButton';

interface RoomPolicyTableEditorProps {
  policyData: ExtraGuestGeneratedPolicies;
  onPolicyDataChange: (data: ExtraGuestGeneratedPolicies) => void;
  roomPolicy: RoomPolicy;
  idOffer: string;
  onGenerateDefault?: () => Promise<void>;
  isGenerating?: boolean;
}

//TODO: Temp will remove next week
const SYNC_ROOM_POLICY_ALLOWED_EMAILS = [
  'gearoid.sheehan@luxuryescapes.com',
  'jessica.bedewi@luxuryescapes.com',
  'min.pangastur@luxuryescapes.com',
  'nathaniel.robinson@luxuryescapes.com',
];

export default function RoomPolicyTableEditor({
  policyData,
  onPolicyDataChange,
  roomPolicy,
  idOffer,
  onGenerateDefault,
  isGenerating = false,
}: RoomPolicyTableEditorProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useCurrentUser();
  const canSyncRoomPolicy = SYNC_ROOM_POLICY_ALLOWED_EMAILS.includes(currentUser?.email);

  const [apiErrors, setApiErrors] = useState<Array<string>>([]);
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [saveBlockedDialogOpen, setSaveBlockedDialogOpen] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const handleCapacitiesChange = useCallback(
    (items: Array<IncludedGuestEntry>) => {
      onPolicyDataChange({ ...policyData, maximumRoomCapacities: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handlePackageInclusionsChange = useCallback(
    (items: Array<IncludedGuestEntry>) => {
      onPolicyDataChange({ ...policyData, packageInclusions: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleGuestItemsChange = useCallback(
    (items: Array<ExtraGuestGeneratedPolicyItem>) => {
      onPolicyDataChange({ ...policyData, extraGuestGeneratedPolicyItems: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleBeddingConfigurationChange = useCallback(
    (value: BeddingConfiguration) => {
      onPolicyDataChange({ ...policyData, beddingConfiguration: value });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleBeddingExtrasChange = useCallback(
    (items: Array<BeddingConfiguration>) => {
      onPolicyDataChange({ ...policyData, beddingConfigurationExtras: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  const handleNotesChange = useCallback(
    (items: Array<string>) => {
      onPolicyDataChange({ ...policyData, additionalPolicyNotes: items });
      setSaveState(buttonStates.default);
    },
    [policyData, onPolicyDataChange],
  );

  // TODO - This will be used when we remove the current restrictions on saving using the new UI
  const handleSave = useCallback(async () => {
    setSaveState(buttonStates.saving);

    const serializedDescription = JSON.stringify(policyData);
    const submitData = {
      room_type_id: roomPolicy.room_type_id,
      description: serializedDescription,
    };

    try {
      if (roomPolicy.id) {
        const { room_type_id: _, ...updateData } = submitData;
        await OffersService.updateRoomPolicy(updateData, idOffer, roomPolicy.id);
      } else {
        await OffersService.createRoomPolicy(submitData, idOffer);
      }
      setSaveState(buttonStates.saved);
      setApiErrors([]);
      enqueueSnackbar('Room policy saved successfully', { variant: 'success' });
    } catch (e) {
      const newApiErrors: Array<string> = [];
      if (e.name === 'ValidationError') {
        e.errors.forEach((err) => {
          newApiErrors.push(err.message);
        });
      }
      setSaveState(buttonStates.failed);
      setApiErrors(newApiErrors);
      reportError(e);
      enqueueSnackbar('Failed to save room policy', { variant: 'error' });
    }
  }, [policyData, roomPolicy, idOffer, enqueueSnackbar]);

  return (
    <Box>
      <Stack spacing={3} divider={<Divider />}>
        <RoomPolicyIncludedGuestsTable
          title="Maximum Room Capacities"
          addButtonLabel="Add capacity"
          items={policyData.maximumRoomCapacities ?? []}
          onChange={handleCapacitiesChange}
        />

        <RoomPolicyIncludedGuestsTable
          title="Package Inclusions"
          addButtonLabel="Add included guest"
          items={policyData.packageInclusions ?? []}
          onChange={handlePackageInclusionsChange}
        />

        <RoomPolicyGuestTable
          items={policyData.extraGuestGeneratedPolicyItems ?? []}
          onChange={handleGuestItemsChange}
          showValidationErrors={hasAttemptedSave}
        />

        <RoomPolicyBeddingEditor
          beddingConfiguration={policyData.beddingConfiguration ?? { bedding: '', icon: '' }}
          beddingExtras={policyData.beddingConfigurationExtras ?? []}
          onBeddingConfigurationChange={handleBeddingConfigurationChange}
          onBeddingExtrasChange={handleBeddingExtrasChange}
        />

        <RoomPolicyListEditor
          title="Additional Policy Notes"
          items={policyData.additionalPolicyNotes ?? []}
          onChange={handleNotesChange}
        />
      </Stack>

      {apiErrors.length > 0 && <ErrorListDisplay messages={apiErrors} />}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          className={saveState}
          onClick={() => {
            setHasAttemptedSave(true);
            setSaveBlockedDialogOpen(true);
          }}
        >
          {buttonMessages[saveState]}
        </Button>
        {canSyncRoomPolicy && onGenerateDefault && (
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={onGenerateDefault}
            disabled={isGenerating}
          >
            {isGenerating ? 'Syncing...' : 'Sync Room Policy'}
          </Button>
        )}
      </Box>

      <Dialog open={saveBlockedDialogOpen} onClose={() => setSaveBlockedDialogOpen(false)}>
        <DialogTitle>Saving Not Available</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Saving room policies with the new structured editor is not yet enabled. This feature is currently under
            development and will be available soon. Please refresh the page to go back to the legacy input.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveBlockedDialogOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
